package com.evoapps.evoshift.shell

import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.Route
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.WindowWidthClass
import kotlinx.datetime.YearMonth

/**
 * SH-161 F3 (Play's Tier 1 Multi-Instance): what a window of its own opens, "Open in new window"
 * on a roster or a workplace. The window has its own workplace and month (its own session, top
 * bar and view models, `di/WindowModule.kt`), so the main window stays where it was.
 */
@Immutable
data class WindowTarget(val workplaceId: String, val rosterId: String? = null) {
    /** The roster's month, which the window's top bar starts on (null: the current month). */
    val month: YearMonth? get() = rosterId?.let(WorkplaceMonthSelection::monthOf)

    /** The window's first screen once the person is signed in. */
    val startRoute: Route get() = rosterId?.let { Route.RosterDetail(it) } ?: Route.Dashboard

    companion object {
        /** The extras a platform window carries its target in (Android's intent extras). */
        const val EXTRA_WORKPLACE = "evoshift:windowWorkplace"
        const val EXTRA_ROSTER = "evoshift:windowRoster"

        fun forWorkplace(workplaceId: String) = WindowTarget(workplaceId)

        /** The window of one roster (`workplaceId_YYYY_MM`); null for an id that is not one. */
        fun forRoster(rosterId: String): WindowTarget? {
            if (WorkplaceMonthSelection.monthOf(rosterId) == null) return null
            val workplaceId = RosterIds.workplaceIdOf(rosterId).takeIf { it.isNotBlank() } ?: return null
            return WindowTarget(workplaceId, rosterId)
        }

        /** Read back from [EXTRA_WORKPLACE] / [EXTRA_ROSTER]; null without a workplace. */
        fun fromExtras(workplaceId: String?, rosterId: String?): WindowTarget? {
            val roster = rosterId?.takeIf { it.isNotBlank() }?.let(::forRoster)
            if (roster != null) return roster
            return workplaceId?.takeIf { it.isNotBlank() }?.let(::forWorkplace)
        }
    }
}

/** Opens a [WindowTarget] in a window of its own; provided where the platform can (Android). */
fun interface NewWindowOpener {
    fun open(target: WindowTarget)
}

/**
 * What the platform offers for new windows: the opener, and whether a mouse or trackpad is
 * attached (a laptop, a tablet with a keyboard cover), where the action shows on any width.
 */
@Immutable
class NewWindows(val opener: NewWindowOpener, val hasPointer: Boolean)

/** Null where windows of their own are not offered (iOS, the web, the desktop, phones' tests). */
val LocalNewWindows = staticCompositionLocalOf<NewWindows?> { null }

/** This window's target; null in the app's main window (the one links and shared files reach). */
val LocalWindowTarget = staticCompositionLocalOf<WindowTarget?> { null }

/**
 * Whether "Open in new window" shows: only on a large window ([WindowWidthClass.isExpanded]) or
 * with a pointer device (a laptop, a tablet with a keyboard cover), where a second window is
 * something people use; not on a phone held in the hand.
 */
fun showsNewWindowAction(width: WindowWidthClass, hasPointer: Boolean): Boolean =
    width.isExpanded || hasPointer

/**
 * "Open in new window" for this window, or null where it is not shown ([showsNewWindowAction]).
 * Measured on the whole window (not a pane's [LocalWindowWidthClass]), so it reads the same in
 * the shell, its sheets and a detail pane.
 */
@Composable
fun rememberOpenInNewWindow(): ((WindowTarget) -> Unit)? {
    val windows = LocalNewWindows.current ?: return null
    val width = with(LocalDensity.current) { LocalWindowInfo.current.containerSize.width.toDp() }
    if (!showsNewWindowAction(WindowWidthClass.fromWidth(width), windows.hasPointer)) return null
    return windows.opener::open
}
